import { useAuth } from '@/components/auth/authProvider';
import { Box, Button, Modal, toast } from '@palmetto/palmetto-components';
import { useState } from 'react';

type ClaimableQueue = {
  assignee?: { id: string; name?: string | null };
};

export function useQueueClaimConflict(queue: ClaimableQueue | undefined | null) {
  const { user } = useAuth();
  const [claimConflictAssigneeName, setClaimConflictAssigneeName] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<(() => void | Promise<void>) | null>(null);
  const [isOverriding, setIsOverriding] = useState(false);

  const dismiss = () => {
    setClaimConflictAssigneeName(null);
    setPendingAction(null);
  };

  /**
   * Runs `action` immediately if the queue is unclaimed or claimed by the current user.
   * If claimed by someone else, shows a confirmation modal instead; on confirm, runs `action`,
   * which is expected to take over the claim itself (e.g. via an assigneeId update).
   *
   * Pass `queueOverride` when the relevant queue is resolved at call time (e.g., after
   * `resolveLatestQueue()`) rather than at hook-initialization time.
   */
  const checkClaimAndProceed = (action: () => void | Promise<void>, queueOverride?: ClaimableQueue | null) => {
    const effectiveQueue = queueOverride ?? queue;
    if (effectiveQueue?.assignee?.id && effectiveQueue.assignee.id !== user?.sub) {
      setClaimConflictAssigneeName(effectiveQueue.assignee.name ?? 'another user');
      setPendingAction(() => action);
      return;
    }
    action();
  };

  const handleConfirmOverride = async () => {
    if (!pendingAction) return;
    setIsOverriding(true);
    try {
      await pendingAction();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error updating queue');
    }
    dismiss();
    setIsOverriding(false);
  };

  const claimConflictModal = claimConflictAssigneeName ? (
    <Modal ariaLabelledBy="claimConflictModal" isOpen={true} onDismiss={dismiss} maxWidth="4xl">
      <Modal.Header id="claimConflictModal" title="Queue Already Claimed" onDismiss={dismiss} />
      <Modal.Body background="secondary" childGap="lg" fontSize="sm">
        <Box>This queue is already claimed by {claimConflictAssigneeName}. Are you sure you want to continue?</Box>
      </Modal.Body>
      <Modal.Footer>
        <Box direction="row" alignItems="flex-end" childGap="sm">
          <Button variant="secondary" tone="neutral" onClick={dismiss} isDisabled={isOverriding}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmOverride}
            isDisabled={isOverriding}
            isLoading={isOverriding}
          >
            Continue Anyway
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  ) : null;

  return { checkClaimAndProceed, claimConflictModal };
}
